/**
 * The two resolvers behind the mark tier (DECISIONS D41, the sheet's
 * contract): a mark takes its contrast from what it sits on. A value on the
 * canvas ground renders as the family's `--mark-*`; a value on a fill
 * renders as the family's `--spectrum-*-dark`. Stored canvases carry the dark
 * stops marks used to be, so resolving on read is the migration. Anything
 * the roster does not know passes through untouched.
 */

import { CANVAS_COLOR_FAMILIES } from "./CanvasColorPalettes";

const NEUTRAL_DARK = "var(--n-50)";
const NEUTRAL_MARK = "var(--mark-neutral)";

const markByDark = new Map<string, string>();
const darkByMark = new Map<string, string>();
const marks = new Set<string>();
const darks = new Set<string>();
for (const f of CANVAS_COLOR_FAMILIES) {
  markByDark.set(f.dark, f.mark);
  darkByMark.set(f.mark, f.dark);
  marks.add(f.mark);
  darks.add(f.dark);
}

/** The family's mark for a value that sits on the ground; idempotent. */
export function markOf(value: string): string {
  if (marks.has(value) || value === NEUTRAL_MARK) return value;
  if (value === NEUTRAL_DARK) return NEUTRAL_MARK;
  return markByDark.get(value) ?? value;
}

/**
 * A shape's stroke or chosen text: the mark on the ground when the shape has
 * no fill, the dark stop on the fill otherwise — the text's existing switch,
 * extended to the stroke.
 */
export function shapeInk(value: string, noFill: boolean): string {
  return noFill ? markOf(value) : darkStopOf(value);
}

/** The family's dark stop for a value that sits on a fill; idempotent. */
export function darkStopOf(value: string): string {
  if (darks.has(value) || value === NEUTRAL_DARK) return value;
  if (value === NEUTRAL_MARK) return NEUTRAL_DARK;
  return darkByMark.get(value) ?? value;
}
